/**
 * Who gets the story-first native first run (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md step 6). Its own module
 * because LandingRoute (main bundle) needs only this rule; the screens' rules (pool, CTA, request) stay in
 * lib/firstRunCore.ts inside the lazy first-run chunk. Pure; lib/firstRun.ts feeds it the live values.
 */
import type { FirstRunVariant } from './metrics';

/** The first run's window after `first_open` — the same 24 h as lib/nativeFirstTap.ts FIRST_TAP_WINDOW_MS (a Jest
 *  test keeps them equal; not imported, so the main bundle doesn't carry the first-tap module). */
export const FIRST_RUN_WINDOW_MS = 24 * 3600_000;

/** The one-shot "done" flag (localStorage, read and written by lib/firstRun.ts). Here, import-free, so
 *  lib/nativeFirstTap.ts can ask whether the first run ended without pulling lib/firstRun.ts's imports (2026-10-02). */
export const FIRST_RUN_DONE_KEY = 'snugtale_first_run_done_v1';
/** The visit's answers for a remount (sessionStorage; components/firstRun/firstRunVisit.ts) — dropped by
 *  lib/firstRun.markFirstRunDone without loading the first-run chunk (2026-10-02 review). */
export const FIRST_RUN_VISIT_KEY = 'snugtale_first_run_visit_v1';

/** Where the first run shows (or not). Times in ms since the epoch. */
export interface FirstRunContext {
  native: boolean;
  signedIn: boolean;
  platform: 'ios' | 'android' | 'web';
  /** Android: Play's neutral age screen was answered (lib/ageGate.ts). */
  ageGateAnswered: boolean;
  /** Android: the age screen was answered with under 13. */
  childDeclared: boolean;
  /** The parent passed the multiplication gate in the last 15 minutes of this app session. */
  parentVerified: boolean;
  /** The one-shot flag (lib/firstRun.ts): the first run already ended with a story, a sign-in, the library, an import,
   *  or (account variant) its choice consumed after the sign-up — not yet at the hand-off itself. */
  done: boolean;
  /** `first_open` of this install (lib/metrics.ts pingFirstOpen), null when missing (= a fresh install). */
  firstOpenAt: number | null;
  now: number;
  /** This device already holds a trial (lib/anonStory.getAnonStory) — it opens its story / the welcome-back path. */
  hasTrialRecord: boolean;
}

/**
 * Which first run this launch gets, or null for the library as before:
 *  - only the native shells, signed out, not done, no trial on the device, within 24 h of first_open
 *    (the native_first_tap window — an old install that signs out later is no first launch);
 *  - Android only after the age screen (it comes first, unchanged — Play Families);
 *  - Android under 13: the account-first variant, and only while the parent's gate is fresh (Kai, 2026-10-02: never an
 *    anonymous trial on a child-declared install, no Google before the gate) — without it the library as today;
 *  - iOS and Android 13+: the story first, no account.
 */
export function firstRunVariant(ctx: FirstRunContext): FirstRunVariant | null {
  if (!ctx.native || ctx.signedIn || ctx.done || ctx.hasTrialRecord) return null;
  if (ctx.firstOpenAt !== null && ctx.now - ctx.firstOpenAt >= FIRST_RUN_WINDOW_MS) return null;
  if (ctx.platform === 'android') {
    if (!ctx.ageGateAnswered) return null;
    if (ctx.childDeclared) return ctx.parentVerified ? 'account' : null;
  }
  return 'trial';
}
